import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { strokeWidthPx } from "./storyEditorUtils";

// Trace un trait (points en fractions du cadre) sur un contexte canvas de W×H pixels.
const drawStroke = (ctx, s, W, H) => {
  const pts = s.points;
  if (!pts?.length) return;
  const w = Math.max(1, s.width * W);
  const path = () => {
    ctx.beginPath();
    const [x0, y0] = [pts[0][0] * W, pts[0][1] * H];
    if (pts.length === 1) { ctx.arc(x0, y0, w / 2, 0, Math.PI * 2); return; }
    ctx.moveTo(x0, y0);
    for (let i = 1; i < pts.length - 1; i++) {
      const [ax, ay] = [pts[i][0] * W, pts[i][1] * H];
      const [bx, by] = [pts[i + 1][0] * W, pts[i + 1][1] * H];
      ctx.quadraticCurveTo(ax, ay, (ax + bx) / 2, (ay + by) / 2);
    }
    const [lx, ly] = [pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H];
    ctx.lineTo(lx, ly);
  };
  const paint = () => { path(); if (pts.length === 1) ctx.fill(); else ctx.stroke(); };
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = w;
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  if (s.tool === "eraser") {
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = ctx.fillStyle = "rgba(0,0,0,1)";
    paint();
  } else if (s.tool === "marker") {
    ctx.globalAlpha = 0.5;
    paint();
  } else if (s.tool === "neon") {
    // Halo coloré + cœur blanc lumineux.
    ctx.shadowColor = s.color;
    ctx.shadowBlur = w * 1.8;
    paint(); paint();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = ctx.fillStyle = "#ffffff";
    ctx.lineWidth = Math.max(1, w * 0.4);
    paint();
  } else {
    paint();
  }
  ctx.restore();
};

const fit = (canvas, frame) => {
  const r = frame.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const W = Math.round(r.width * dpr), H = Math.round(r.height * dpr);
  if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
  return { W, H };
};

// Calque de dessin main libre : canvas « validé » (tous les traits) + canvas « en cours » (trait
// en train d'être tracé). Les points sont stockés en fractions du cadre → rejouables à toute taille.
// `baseUrl` = dessin déjà enregistré (édition d'une annonce) : dessiné en premier, la gomme agit dessus aussi.
export const StoryDrawLayer = forwardRef(({ strokes, active, tool, color, thickness, onCommit, frameRef, baseUrl = null }, ref) => {
  const baseRef = useRef(null);
  const liveRef = useRef(null);
  const cur = useRef(null);
  const baseImg = useRef(null);
  const [baseReady, setBaseReady] = useState(0);

  useEffect(() => {
    baseImg.current = null;
    if (!baseUrl) { setBaseReady((n) => n + 1); return undefined; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { baseImg.current = img; setBaseReady((n) => n + 1); };
    img.onerror = () => setBaseReady((n) => n + 1);
    img.src = resolveMediaUrl(baseUrl);
    return () => { img.onload = null; img.onerror = null; };
  }, [baseUrl]);

  const replay = useCallback(() => {
    const c = baseRef.current, f = frameRef.current;
    if (!c || !f) return;
    const { W, H } = fit(c, f);
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    if (baseImg.current) { try { ctx.drawImage(baseImg.current, 0, 0, W, H); } catch { /* image non exploitable */ } }
    strokes.forEach((s) => drawStroke(ctx, s, W, H));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, frameRef, baseReady]);

  useEffect(() => { replay(); }, [replay]);
  useEffect(() => {
    const f = frameRef.current;
    if (!f || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => replay());
    ro.observe(f);
    return () => ro.disconnect();
  }, [replay, frameRef]);

  useImperativeHandle(ref, () => ({
    // PNG transparent du calque (dessin de base + traits) ; null si vide.
    exportPng: () => new Promise((resolve) => {
      if ((!strokes.length && !baseImg.current) || !baseRef.current) { resolve(null); return; }
      try { baseRef.current.toBlob((b) => resolve(b), "image/png"); } catch { resolve(null); }
    }),
  }), [strokes]);

  const toFrac = (e) => {
    const r = frameRef.current.getBoundingClientRect();
    return [Math.round(((e.clientX - r.left) / r.width) * 10000) / 10000, Math.round(((e.clientY - r.top) / r.height) * 10000) / 10000];
  };
  const paintLive = () => {
    const s = cur.current;
    if (!s) return;
    if (s.tool === "eraser") {
      // La gomme agit directement sur le calque validé (aperçu immédiat), segment par segment.
      const c = baseRef.current, { W, H } = fit(c, frameRef.current);
      const tail = s.points.slice(-3);
      drawStroke(c.getContext("2d"), { ...s, points: tail }, W, H);
      return;
    }
    const c = liveRef.current, { W, H } = fit(c, frameRef.current);
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    drawStroke(ctx, s, W, H);
  };
  const down = (e) => {
    if (cur.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const r = frameRef.current.getBoundingClientRect();
    cur.current = { pid: e.pointerId, tool, color, width: strokeWidthPx(tool, thickness) / r.width, points: [toFrac(e)] };
    paintLive();
  };
  const move = (e) => {
    const s = cur.current;
    if (!s || s.pid !== e.pointerId) return;
    const p = toFrac(e);
    const last = s.points[s.points.length - 1];
    if (Math.abs(p[0] - last[0]) < 0.002 && Math.abs(p[1] - last[1]) < 0.002) return;
    s.points.push(p);
    paintLive();
  };
  const up = (e) => {
    const s = cur.current;
    if (!s || s.pid !== e.pointerId) return;
    cur.current = null;
    const c = liveRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    const { pid, ...stroke } = s;
    onCommit(stroke);
  };

  return (
    <>
      <canvas ref={baseRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="story-fs-draw-canvas" data-strokes={strokes.length} data-base={baseUrl ? 1 : 0} />
      {active && (
        <canvas
          ref={liveRef}
          className="absolute inset-0 w-full h-full touch-none z-20"
          style={{ cursor: "crosshair" }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          data-testid="story-fs-draw-surface"
        />
      )}
    </>
  );
});
StoryDrawLayer.displayName = "StoryDrawLayer";
